import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mic, MicOff, Send, Volume2, VolumeX, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
    bg: "#050403",
    glass: "rgba(20, 18, 15, 0.55)",
    gold: "#D4AF37",
    goldGlow: "#FFDF73",
    warmYellow: "#FFD54F",
    emberOrange: "#FF7A18",
    amber: "#B4713A",
    violet: "#A855F7",
    text: "#F2EBE1",
    textMuted: "#B8B0A6",
    textFaint: "#7A756D",
    border: "rgba(212, 175, 55, 0.18)",
};

// Simplified orb — same solar aesthetic
const ReportOrb = ({ speaking, recording, transcribing, onClick }) => {
    const state = recording ? "recording" : transcribing ? "transcribing" : speaking ? "speaking" : "idle";
    const accent = state === "recording" ? C.emberOrange : state === "transcribing" ? C.violet : state === "speaking" ? C.goldGlow : C.warmYellow;
    return (
        <div
            data-testid="report-orb"
            onClick={onClick}
            role="button"
            style={{ position: "absolute", top: "6%", left: "50%", transform: "translateX(-50%)", width: "min(560px, 62vw)", height: "min(560px, 62vw)", maxWidth: 560, maxHeight: 560, cursor: "pointer", zIndex: 3, userSelect: "none" }}
            title={recording ? "Tap to stop" : "Tap to speak"}
        >
            <motion.div animate={{ scale: speaking || recording ? [1, 1.12, 1] : [1, 1.06, 1], opacity: speaking || recording ? [0.6, 1, 0.6] : [0.4, 0.7, 0.4] }} transition={{ duration: speaking ? 0.9 : recording ? 1.1 : 5.5, repeat: Infinity, ease: "easeInOut" }} style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle at 50% 50%, ${accent}99, ${C.goldGlow}66 15%, ${C.gold}44 35%, transparent 78%)`, filter: "blur(45px)" }} />
            {[0, 1, 2].map((i) => (
                <motion.div key={i} animate={{ scale: [0.7, 1.5], opacity: [0.75, 0] }} transition={{ duration: 3, delay: i * 0.9, repeat: Infinity, ease: "easeOut" }} style={{ position: "absolute", inset: "18%", borderRadius: "50%", border: `1.5px solid ${i % 2 === 0 ? C.warmYellow : C.goldGlow}`, boxShadow: `0 0 22px ${accent}aa` }} />
            ))}
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 50, repeat: Infinity, ease: "linear" }} style={{ position: "absolute", inset: "3%", borderRadius: "50%", border: `1px solid ${C.gold}55`, boxShadow: `inset 0 0 40px ${C.warmYellow}22` }} />
            <motion.div animate={{ scale: recording ? [1, 1.28, 1] : speaking ? [1, 1.2, 1] : [1, 1.08, 1] }} transition={{ duration: recording ? 0.7 : speaking ? 0.9 : 3.5, repeat: Infinity, ease: "easeInOut" }} style={{ position: "absolute", top: "35%", left: "35%", width: "30%", height: "30%", borderRadius: "50%", background: `radial-gradient(circle at 40% 35%, #FFFAE0, ${C.goldGlow} 20%, ${C.warmYellow} 45%, ${C.gold} 70%, ${C.amber} 92%, ${C.bg} 100%)`, boxShadow: `0 0 60px ${accent}, 0 0 120px ${C.warmYellow}aa, inset 0 0 40px ${C.goldGlow}` }} />
            <div style={{ position: "absolute", bottom: "-6%", left: 0, right: 0, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, letterSpacing: "0.36em", textTransform: "uppercase", color: accent, textShadow: `0 0 14px ${accent}`, fontWeight: 500 }}>
                {state === "recording" ? "◉ LISTENING · TAP TO STOP" : state === "transcribing" ? "◈ PROCESSING" : state === "speaking" ? "◊ REPORTING" : "◎ TAP TO REQUEST REPORT"}
            </div>
        </div>
    );
};

export default function MidasReportPage() {
    // Fresh session on every visit — no history leakage across LPs
    const [sessionId] = useState(() => `report-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [voiceOn, setVoiceOn] = useState(true);
    const [speaking, setSpeaking] = useState(false);
    const audioRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingStartRef = useRef(0);
    const audioUnlockedRef = useRef(false);
    const audioCtxRef = useRef(null);

    useEffect(() => {
        document.body.style.background = C.bg;
        // ── iOS SAFARI FIX ──
        // Persistent audio element reused across all TTS playback (iOS requires same element).
        if (!window.__midasAudioEl) {
            window.__midasAudioEl = new Audio();
            window.__midasAudioEl.preload = "auto";
        }
        let unlocked = false;
        const unlock = () => {
            if (unlocked) return;
            try {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                if (AudioCtx) {
                    const ctx = new AudioCtx();
                    const buffer = ctx.createBuffer(1, 1, 22050);
                    const source = ctx.createBufferSource();
                    source.buffer = buffer;
                    source.connect(ctx.destination);
                    source.start(0);
                    if (ctx.resume) ctx.resume();
                    audioCtxRef.current = ctx;
                }
                // Prime the persistent element SYNCHRONOUSLY inside the gesture
                const el = window.__midasAudioEl;
                el.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjIwLjEwMAAAAAAAAAAAAAAA//tQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD///////////////////////////////////////////8AAAAATGF2YzU4LjM1AAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//tCxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
                el.muted = false;
                el.volume = 1;
                const p = el.play();
                if (p && typeof p.then === "function") {
                    p.then(() => { try { el.pause(); } catch {} }).catch(() => {});
                }
                unlocked = true;
                audioUnlockedRef.current = true;
            } catch {}
        };
        window.addEventListener("click", unlock, { once: false });
        window.addEventListener("touchstart", unlock, { once: false });
        window.addEventListener("keydown", unlock, { once: false });
        return () => {
            window.removeEventListener("click", unlock);
            window.removeEventListener("touchstart", unlock);
            window.removeEventListener("keydown", unlock);
        };
    }, []);

    // Copied verbatim from working /midas — no AudioContext, no unlock hack, just Audio().play()
    // COPIED VERBATIM FROM /midas — which works on iPhone Safari + Chrome + Samsung
    const playSpeech = useCallback(async (text) => {
        if (!voiceOn) return;
        try {
            setSpeaking(true);
            const r = await axios.post(
                `${API}/midas/voice/speak`,
                { text, voice: "fable", speed: 1.1 },
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(r.data);
            // Use the persistent audio element (unlocked at page mount for iOS Safari)
            // Fresh Audio() would fail on iOS — must reuse the primed element.
            const audio = window.__midasAudioEl || new Audio();
            audio.src = url;
            audio.preload = "auto";
            audio.onended = () => {
                setSpeaking(false);
                URL.revokeObjectURL(url);
            };
            audio.onerror = (e) => {
                console.error("[report audio error]", e);
                setSpeaking(false);
            };
            try {
                audio.load();
                await audio.play();
                audioRef.current = audio;
            } catch (playErr) {
                console.error("[report play blocked]", playErr);
                setSpeaking(false);
            }
        } catch (e) {
            console.error("[report speech error]", e);
            setSpeaking(false);
        }
    }, [voiceOn]);

    // No-op stub for API compat — nothing to unlock, /midas approach works without it
    const unlockAudio = useCallback(() => {}, []);

    // COPIED VERBATIM FROM /midas — straight single call, no retry loop
    const send = async (text) => {
        if (!text.trim() || sending) return;
        setSending(true);
        setMessages((m) => [...m, { role: "user", content: text }]);
        setInput("");
        try {
            const r = await axios.post(`${API}/midas/chat`, {
                session_id: sessionId,
                message: text,
                mode: "report",
            });
            const reply = r.data.reply;
            setMessages((m) => [...m, { role: "assistant", content: reply }]);
            playSpeech(reply);
        } catch (e) {
            console.error("[report send error]", e);
            setMessages((m) => [...m, { role: "assistant", content: "One moment — please tap the orb again." }]);
        }
        setSending(false);
    };

    const startRecording = async () => {
        if (recording || transcribing || sending) return;
        recordingStartRef.current = Date.now();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1, sampleRate: 16000 } });
            // Browser-adaptive mimeType detection — Safari/iOS need mp4, Chrome/Firefox use webm
            const candidates = [
                "audio/webm;codecs=opus",
                "audio/webm",
                "audio/mp4;codecs=mp4a.40.2",
                "audio/mp4",
                "audio/ogg;codecs=opus",
                "audio/mpeg",
            ];
            let chosenMime = "";
            for (const m of candidates) {
                if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
                    chosenMime = m;
                    break;
                }
            }
            const recorderOpts = chosenMime ? { mimeType: chosenMime, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 };
            const mr = new MediaRecorder(stream, recorderOpts);
            const usedMime = mr.mimeType || chosenMime || "audio/webm";
            const ext = usedMime.includes("mp4") ? "m4a" : usedMime.includes("ogg") ? "ogg" : usedMime.includes("mpeg") ? "mp3" : "webm";
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                if (Date.now() - recordingStartRef.current < 500) { setTranscribing(false); return; }
                const blob = new Blob(audioChunksRef.current, { type: usedMime });
                if (blob.size < 1500) { setTranscribing(false); return; }
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("audio", blob, `voice.${ext}`);
                    const r = await axios.post(`${API}/midas/voice/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
                    if (!r.data.silent && r.data.text?.trim()) {
                        await send(r.data.text.trim());
                    } else {
                        console.warn("[report] Transcribe returned silent/empty:", r.data);
                    }
                } catch (err) {
                    const msg = err?.response?.data?.detail || err?.message || "Transcribe failed";
                    console.error("[report] Transcribe error:", msg);
                    setMessages((m) => [...m, { role: "assistant", content: "Voice not captured this time — please try typing your request." }]);
                }
                setTranscribing(false);
            };
            mediaRecorderRef.current = mr; mr.start(); setRecording(true);
        } catch (err) {
            console.error("[report] Mic access denied or unsupported:", err);
            alert("Voice input not available on this browser. Please type your request instead, or try Chrome / Edge.");
        }
    };
    const stopRecording = () => { if (mediaRecorderRef.current && recording) { mediaRecorderRef.current.stop(); setRecording(false); } };
    const stopSpeech = () => {
        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch {}
        }
        setSpeaking(false);
    };
    // Safari-safe audio unlock: fires synchronously inside the click handler.
    // Must NOT be wrapped in async/setTimeout — Safari requires the .play() call
    // to be in the same synchronous stack frame as the user gesture.
    const primeAudioSync = () => {
        try {
            const el = new Audio("data:audio/mp3;base64,//MYxAAAAAAAAAAAAFhpbmcAAAAPAAAAAgAAA6QA9PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT0/////////////////////////////////////////////////////////////////8AAAA5TEFNRTMuMTAwAc0AAAAAAAAAABSAJAJAQgAAgAAAA6ScU2XkAAAAAAAAAAAAAAAAAAAA//MYxAAAAAAAAAAAAFhpbmcAAAAPAAAAAgAAA6QA9PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT0/////////////////////////////////////////////////////////////////8AAAA5TEFNRTMuMTAwAc0AAAAAAAAAABSAJAJAQgAAgAAAA6ScU2XkAAAAAAAAAAAAAAAAAAAA");
            el.volume = 0;
            const p = el.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
            audioUnlockedRef.current = true;
        } catch {}
    };
    const handleOrbClick = () => {
        // Priority 1: if speaking, stop the voice (user wants to interrupt)
        if (speaking) { stopSpeech(); return; }
        // Priority 2: synchronous audio unlock — must be in same stack frame as click
        primeAudioSync();
        // Priority 3: toggle recording
        if (recording) stopRecording();
        else if (!transcribing && !sending) startRecording();
    };

    return (
        <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 18%, ${C.gold}16, transparent 55%), ${C.bg}`, color: C.text, fontFamily: "'Manrope', sans-serif", position: "relative", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ padding: "26px 40px", borderBottom: `1px solid ${C.gold}10`, display: "flex", alignItems: "baseline", justifyContent: "space-between", position: "relative", zIndex: 10 }}>
                <div>
                    <div data-testid="report-brand" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, letterSpacing: "0.32em", color: C.goldGlow, textShadow: `0 0 24px ${C.gold}88` }}>MIDAS TOUCH</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: C.textMuted, letterSpacing: "0.22em", textTransform: "uppercase", marginTop: 6 }}>Bot Report · Voice Interface</div>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: C.textMuted, letterSpacing: "0.14em" }}>{new Date().toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Dubai" })} DXB</div>
            </div>

            <main style={{ position: "relative", minHeight: "calc(100vh - 100px)", padding: "20px 40px" }}>
                <ReportOrb speaking={speaking} recording={recording} transcribing={transcribing} onClick={handleOrbClick} />

                {/* Instruction card while empty */}
                {messages.length === 0 && (
                    <div style={{ position: "absolute", left: "50%", top: "72%", transform: "translateX(-50%)", textAlign: "center", maxWidth: 640 }}>
                        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.textMuted, fontStyle: "italic", letterSpacing: "0.05em", lineHeight: 1.5 }}>
                            Tap the orb and request the report.<br />
                            <span style={{ fontSize: 15, color: C.textFaint }}>Try: "Give me the current bot status" · "What's the P&L today?" · "Explain the risk architecture"</span>
                        </div>
                    </div>
                )}

                {/* Transcript panel — collapsible on mobile so orb stays tappable */}
                {messages.length > 0 && (
                    <div data-testid="report-transcript" style={{ position: "fixed", left: 12, right: 12, bottom: 100, maxHeight: "min(38vh, 300px)", background: C.glass, backdropFilter: "blur(24px)", border: `1px solid ${C.border}`, borderRadius: 6, zIndex: 6, padding: "16px 20px", overflowY: "auto", userSelect: "text", boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 40px ${C.gold}12` }}>
                        {messages.map((m, i) => (
                            <div key={i} style={{ marginBottom: 18, paddingLeft: m.role === "user" ? 40 : 0, paddingRight: m.role === "assistant" ? 40 : 0 }}>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: m.role === "user" ? C.warmYellow : C.goldGlow, marginBottom: 6 }}>{m.role === "user" ? "REQUEST" : "MIDAS TOUCH"}</div>
                                <div style={{ fontFamily: m.role === "user" ? "'Manrope', sans-serif" : "'Cormorant Garamond', serif", fontSize: m.role === "user" ? 14 : 17, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{m.content}</div>
                            </div>
                        ))}
                        {sending && <div style={{ display: "flex", gap: 5 }}>{[0, 1, 2].map((i) => (<motion.div key={i} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} style={{ width: 5, height: 5, borderRadius: "50%", background: C.warmYellow }} />))}</div>}
                    </div>
                )}

                {/* Input bar */}
                <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 20, width: "min(720px, 92vw)", zIndex: 9 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.glass, backdropFilter: "blur(20px)", border: `1px solid ${C.warmYellow}33`, borderRadius: 999, padding: "6px 6px 6px 22px", boxShadow: `0 12px 40px rgba(0,0,0,0.5), 0 0 30px ${C.gold}22` }}>
                        <input data-testid="report-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { unlockAudio(); if (e.key === "Enter" && !sending) send(input); }} placeholder={transcribing ? "Processing..." : recording ? "Listening... tap orb to stop" : "Tap the orb or type your request"} disabled={sending || recording || transcribing} style={{ flex: 1, background: "transparent", border: "none", color: C.text, fontSize: 16, fontFamily: "'Manrope', sans-serif", padding: "12px 0", outline: "none" }} />
                        <button data-testid="report-voice-toggle" onClick={() => { unlockAudio(); setVoiceOn((v) => !v); }} style={{ background: "transparent", border: "none", color: voiceOn ? C.goldGlow : C.textFaint, width: 38, height: 38, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{voiceOn ? <Volume2 size={17} /> : <VolumeX size={17} />}</button>
                        <button data-testid="report-mic" onClick={() => { unlockAudio(); recording ? stopRecording() : startRecording(); }} disabled={sending || transcribing} style={{ background: recording ? `linear-gradient(135deg, ${C.emberOrange}, ${C.warmYellow})` : "transparent", border: `1px solid ${recording ? C.warmYellow : C.gold + "44"}`, color: recording ? C.bg : C.goldGlow, width: 42, height: 42, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: recording ? `0 0 24px ${C.warmYellow}aa` : "none" }}>{transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <MicOff size={17} /> : <Mic size={17} />}</button>
                        <button data-testid="report-send" onClick={() => { unlockAudio(); send(input); }} disabled={sending || !input.trim()} style={{ background: input.trim() ? `linear-gradient(135deg, ${C.warmYellow}, ${C.goldGlow})` : "transparent", border: `1px solid ${C.gold}44`, color: input.trim() ? C.bg : C.goldGlow, width: 42, height: 42, borderRadius: "50%", cursor: input.trim() ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center" }}><Send size={16} /></button>
                    </div>
                </div>
            </main>
        </div>
    );
}
